import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Save, GripVertical, FileText } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface Day {
  day: number;
  title: string;
  tasks: string[];
}

interface Week {
  week: number;
  title: string;
  days: Day[];
}

interface RoadmapEditorProps {
  blogPostId: string;
}

export const RoadmapEditor = ({ blogPostId }: RoadmapEditorProps) => {
  const [weeks, setWeeks] = useState<Week[]>([]);
  const [loading, setLoading] = useState(false);
  const [roadmapId, setRoadmapId] = useState<string | null>(null);
  const [bulkInput, setBulkInput] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchRoadmap();
  }, [blogPostId]);

  const fetchRoadmap = async () => {
    if (blogPostId === "new") return;

    try {
      const { data } = await (supabase as any)
        .from("roadmaps")
        .select("id, content")
        .eq("blog_post_id", blogPostId)
        .maybeSingle();

      if (data?.content && Array.isArray(data.content)) {
        setWeeks(data.content as unknown as Week[]);
        setRoadmapId(data.id);
      }
    } catch (error) {
      console.error("Error fetching roadmap:", error);
    }
  };

  const handleSave = async () => {
    if (blogPostId === "new") {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please save the blog post first before creating a roadmap.",
      });
      return;
    }

    setLoading(true);
    try {
      if (roadmapId) {
        // Update existing roadmap
        const { error } = await (supabase as any)
          .from("roadmaps")
          .update({ content: weeks as any })
          .eq("id", roadmapId);

        if (error) throw error;
      } else {
        // Create new roadmap
        const { data, error } = await (supabase as any)
          .from("roadmaps")
          .insert([{ blog_post_id: blogPostId, content: weeks as any }])
          .select()
          .single();

        if (error) throw error;
        if (data) setRoadmapId(data.id);
      }

      toast({
        title: "Success",
        description: "Roadmap saved successfully.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save roadmap.",
      });
    } finally {
      setLoading(false);
    }
  };

  const addWeek = () => {
    const newWeek: Week = {
      week: weeks.length + 1,
      title: `Week ${weeks.length + 1} Title`,
      days: [],
    };
    setWeeks([...weeks, newWeek]);
  };

  const removeWeek = (weekIndex: number) => {
    const updated = weeks.filter((_, i) => i !== weekIndex);
    // Renumber weeks
    const renumbered = updated.map((week, i) => ({ ...week, week: i + 1 }));
    setWeeks(renumbered);
  };

  const updateWeek = (weekIndex: number, field: keyof Week, value: any) => {
    const updated = [...weeks];
    updated[weekIndex] = { ...updated[weekIndex], [field]: value };
    setWeeks(updated);
  };

  const addDay = (weekIndex: number) => {
    const updated = [...weeks];
    const currentDays = updated[weekIndex].days.length;
    const newDay: Day = {
      day: currentDays + 1,
      title: `Day ${currentDays + 1} Title`,
      tasks: [],
    };
    updated[weekIndex].days.push(newDay);
    setWeeks(updated);
  };

  const removeDay = (weekIndex: number, dayIndex: number) => {
    const updated = [...weeks];
    updated[weekIndex].days = updated[weekIndex].days.filter((_, i) => i !== dayIndex);
    // Renumber days
    updated[weekIndex].days = updated[weekIndex].days.map((day, i) => ({
      ...day,
      day: i + 1,
    }));
    setWeeks(updated);
  };

  const updateDay = (weekIndex: number, dayIndex: number, field: keyof Day, value: any) => {
    const updated = [...weeks];
    updated[weekIndex].days[dayIndex] = {
      ...updated[weekIndex].days[dayIndex],
      [field]: value,
    };
    setWeeks(updated);
  };

  const addTask = (weekIndex: number, dayIndex: number) => {
    const updated = [...weeks];
    updated[weekIndex].days[dayIndex].tasks.push("");
    setWeeks(updated);
  };

  const updateTask = (weekIndex: number, dayIndex: number, taskIndex: number, value: string) => {
    const updated = [...weeks];
    updated[weekIndex].days[dayIndex].tasks[taskIndex] = value;
    setWeeks(updated);
  };

  const removeTask = (weekIndex: number, dayIndex: number, taskIndex: number) => {
    const updated = [...weeks];
    updated[weekIndex].days[dayIndex].tasks = updated[weekIndex].days[dayIndex].tasks.filter(
      (_, i) => i !== taskIndex
    );
    setWeeks(updated);
  };

  const parseBulkInput = () => {
    try {
      const lines = bulkInput.trim().split("\n");
      const parsedWeeks: Week[] = [];
      let currentWeek: Week | null = null;
      let currentDay: Day | null = null;

      lines.forEach((line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        // Week pattern: "Week X: Title" or "Week X - Title"
        const weekMatch = trimmedLine.match(/^Week\s+(\d+)[:\-\s]+(.+)$/i);
        if (weekMatch) {
          if (currentDay && currentWeek) {
            currentWeek.days.push(currentDay);
            currentDay = null;
          }
          if (currentWeek) {
            parsedWeeks.push(currentWeek);
          }
          currentWeek = {
            week: parseInt(weekMatch[1]),
            title: weekMatch[2].trim(),
            days: [],
          };
          return;
        }

        // Day pattern: "Day X: Title" or "Day X - Title"
        const dayMatch = trimmedLine.match(/^Day\s+(\d+)[:\-\s]+(.+)$/i);
        if (dayMatch) {
          if (currentDay && currentWeek) {
            currentWeek.days.push(currentDay);
          }
          currentDay = {
            day: parseInt(dayMatch[1]),
            title: dayMatch[2].trim(),
            tasks: [],
          };
          return;
        }

        // Task pattern: starts with "-" or "*" or a number followed by "."
        if (currentDay && (trimmedLine.startsWith("-") || trimmedLine.startsWith("*") || /^\d+\./.test(trimmedLine))) {
          const task = trimmedLine.replace(/^[-*]\s*/, "").replace(/^\d+\.\s*/, "").trim();
          if (task) {
            currentDay.tasks.push(task);
          }
        }
      });

      // Add last day and week
      if (currentDay && currentWeek) {
        currentWeek.days.push(currentDay);
      }
      if (currentWeek) {
        parsedWeeks.push(currentWeek);
      }

      if (parsedWeeks.length === 0) {
        toast({
          variant: "destructive",
          title: "Parse Error",
          description: "Could not parse the input. Please check the format.",
        });
        return;
      }

      setWeeks(parsedWeeks);
      setBulkInput("");
      setDialogOpen(false);
      toast({
        title: "Success",
        description: `Imported ${parsedWeeks.length} weeks successfully.`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to parse the input. Please check the format.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>28-Day Learning Roadmap</CardTitle>
          <CardDescription>
            Create a structured 28-day learning path for this article. Organize it into weeks and days
            with specific tasks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button type="button" variant="outline" className="gap-2">
                  <FileText className="h-4 w-4" />
                  Bulk Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Import Full 28-Day Roadmap</DialogTitle>
                  <DialogDescription>
                    Paste your complete roadmap script below. Format example:
                    <pre className="mt-2 p-3 bg-muted rounded-md text-xs overflow-x-auto">
{`Week 1: Getting Started
Day 1: Introduction
- Task 1
- Task 2
Day 2: Setup
- Task 1

Week 2: Advanced Topics
Day 1: Deep Dive
- Task 1`}
                    </pre>
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <Textarea
                    value={bulkInput}
                    onChange={(e) => setBulkInput(e.target.value)}
                    placeholder="Paste your roadmap here..."
                    className="min-h-[400px] font-mono text-sm"
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setBulkInput("");
                        setDialogOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="button" onClick={parseBulkInput}>
                      Import Roadmap
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button onClick={addWeek} type="button" variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />
              Add Week
            </Button>
            <Button onClick={handleSave} disabled={loading} type="button" className="gap-2">
              <Save className="h-4 w-4" />
              {loading ? "Saving..." : "Save Roadmap"}
            </Button>
          </div>

          {weeks.length === 0 && (
            <div className="rounded-lg border border-dashed border-border p-12 text-center">
              <p className="text-muted-foreground">
                No roadmap created yet. Click "Add Week" to get started.
              </p>
            </div>
          )}

          <Accordion type="multiple" className="space-y-4">
            {weeks.map((week, weekIndex) => (
              <AccordionItem key={weekIndex} value={`week-${weekIndex}`} className="border rounded-lg">
                <AccordionTrigger className="px-4 hover:no-underline">
                  <div className="flex items-center gap-2 flex-1">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold">
                      Week {week.week}: {week.title}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-4">
                  <div className="space-y-2">
                    <Label>Week Title</Label>
                    <div className="flex gap-2">
                      <Input
                        value={week.title}
                        onChange={(e) => updateWeek(weekIndex, "title", e.target.value)}
                        placeholder="Week title"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        onClick={() => removeWeek(weekIndex)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3 border-l-2 border-primary pl-4">
                    {week.days.map((day, dayIndex) => (
                      <Card key={dayIndex}>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <Label>Day {day.day} Title</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeDay(weekIndex, dayIndex)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <Input
                            value={day.title}
                            onChange={(e) => updateDay(weekIndex, dayIndex, "title", e.target.value)}
                            placeholder="Day title"
                          />
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <Label>Tasks</Label>
                          {day.tasks.map((task, taskIndex) => (
                            <div key={taskIndex} className="flex gap-2">
                              <Input
                                value={task}
                                onChange={(e) =>
                                  updateTask(weekIndex, dayIndex, taskIndex, e.target.value)
                                }
                                placeholder="Task description"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeTask(weekIndex, dayIndex, taskIndex)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => addTask(weekIndex, dayIndex)}
                            className="gap-2"
                          >
                            <Plus className="h-3 w-3" />
                            Add Task
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => addDay(weekIndex)}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Day
                  </Button>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
};
